import { useState, useCallback, useMemo } from 'react'
import { AlertTriangle, PencilIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { type SignificantHazard } from '@/shared/hooks/api/standards'
import { useUpdateUserStandardSignificantHazards } from '@/shared/hooks/api/userStandards'
import {
  resolveHazardAvailableLocales,
  resolveLocalizedHazardField,
} from '@/shared/lib/standardEntityLocalizations'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useEntityDisplayLocales } from '../hooks/useEntityDisplayLocales'
import { useStandardSectionExtraction } from '../hooks/useStandardSectionExtraction'
import type { StandardDetailSectionProps } from '../standardDetailSectionProps'
import { isSignificantHazardsConfirmedEmpty } from '../utils'
import { SaveAsExtractionGoldButton } from './SaveAsExtractionGoldButton'
import { SignificantHazardForm } from './SignificantHazardForm'
import {
  type SignificantHazardFormData,
  validateSignificantHazardForm,
} from './SignificantHazardForm.schema'
import { StandardEntityLocaleToggle } from './StandardEntityLocaleToggle'
import { StandardSectionEmptyState } from './StandardSectionEmptyState'
import { StandardSectionExtractControls } from './StandardSectionExtractControls'

export type StandardSignificantHazardsSectionProps = StandardDetailSectionProps

type DialogState = { type: 'edit'; hazardId: string } | { type: 'add' } | null

function hazardToFormData(
  hazard: SignificantHazard
): SignificantHazardFormData {
  return {
    number: hazard.number?.trim() ?? '',
    description: hazard.description?.trim() ?? '',
    category: hazard.category?.trim() ?? '',
    examplesOfHazardousSituations:
      hazard.examplesOfHazardousSituations?.trim() ?? '',
    associatedActivity: hazard.associatedActivity?.trim() ?? '',
    relatedDangerZone: hazard.relatedDangerZone?.trim() ?? '',
    clauseReference: hazard.clauseReference?.trim() ?? '',
  }
}

function formDataToHazard(formData: SignificantHazardFormData) {
  return {
    number: formData.number.trim(),
    description: formData.description.trim(),
    category: formData.category.trim() || undefined,
    examplesOfHazardousSituations:
      formData.examplesOfHazardousSituations.trim() || undefined,
    associatedActivity: formData.associatedActivity.trim() || undefined,
    relatedDangerZone: formData.relatedDangerZone.trim() || undefined,
    clauseReference: formData.clauseReference.trim() || undefined,
  }
}

const emptyFormData: SignificantHazardFormData = {
  number: '',
  description: '',
  category: '',
  examplesOfHazardousSituations: '',
  associatedActivity: '',
  relatedDangerZone: '',
  clauseReference: '',
}

export function StandardSignificantHazardsSection({
  standard,
  hasPdf,
  isPdfUploading,
  onUploadPdf,
}: StandardSignificantHazardsSectionProps) {
  const extraction = useStandardSectionExtraction(
    standard,
    'significantHazards'
  )
  const { t } = useTranslation('standardsLibrary')
  const { getDisplayLocale, selectItemLocale } = useEntityDisplayLocales()
  const updateSignificantHazards = useUpdateUserStandardSignificantHazards(
    standard.id
  )
  const [dialogState, setDialogState] = useState<DialogState>(null)
  const [formData, setFormData] =
    useState<SignificantHazardFormData>(emptyFormData)

  const currentHazards = standard.significantHazards?.hazards ?? []
  const confirmedEmpty = isSignificantHazardsConfirmedEmpty(standard)

  const openEditDialog = useCallback((hazard: SignificantHazard) => {
    setFormData(hazardToFormData(hazard))
    setDialogState({ type: 'edit', hazardId: hazard.id })
  }, [])

  const openAddDialog = useCallback(() => {
    setFormData(emptyFormData)
    setDialogState({ type: 'add' })
  }, [])

  const closeDialog = useCallback(() => {
    setDialogState(null)
  }, [])

  const updateHazard = useCallback(() => {
    const validation = validateSignificantHazardForm(formData)
    if (!validation.success) {
      return
    }
    if (dialogState?.type !== 'edit') {
      return
    }
    const baseHazards = standard.significantHazards?.hazards ?? []
    const editIndex = baseHazards.findIndex(
      (hazard) => hazard.id === dialogState.hazardId
    )
    if (editIndex === -1) {
      return
    }
    const normalized = formDataToHazard(formData)
    const newHazards = baseHazards.slice()
    newHazards[editIndex] = {
      ...normalized,
      id: baseHazards[editIndex].id,
    }
    updateSignificantHazards.mutate(
      { hazards: newHazards },
      { onSuccess: closeDialog }
    )
  }, [
    formData,
    dialogState,
    standard.significantHazards?.hazards,
    updateSignificantHazards,
    closeDialog,
  ])

  const createHazard = useCallback(() => {
    const validation = validateSignificantHazardForm(formData)
    if (!validation.success) {
      return
    }
    const baseHazards = standard.significantHazards?.hazards ?? []
    const normalized = formDataToHazard(formData)
    const newHazard: SignificantHazard = {
      ...normalized,
      id: crypto.randomUUID(),
    }
    const newHazards: SignificantHazard[] = [...baseHazards, newHazard]
    updateSignificantHazards.mutate(
      { hazards: newHazards },
      { onSuccess: closeDialog }
    )
  }, [
    formData,
    standard.significantHazards?.hazards,
    updateSignificantHazards,
    closeDialog,
  ])

  const deleteCurrentHazard = useCallback(() => {
    if (dialogState?.type !== 'edit') {
      return
    }
    const baseHazards = standard.significantHazards?.hazards ?? []
    const newHazards = baseHazards.filter(
      (hazard) => hazard.id !== dialogState.hazardId
    )
    updateSignificantHazards.mutate(
      { hazards: newHazards },
      { onSuccess: closeDialog }
    )
  }, [
    dialogState,
    standard.significantHazards?.hazards,
    updateSignificantHazards,
    closeDialog,
  ])

  const isDialogOpen = dialogState !== null
  const isEdit = dialogState?.type === 'edit'
  const dialogTitle = isEdit ? t('detail.editHazard') : t('detail.addHazard')

  const isSaveDisabled = useMemo(() => {
    const validation = validateSignificantHazardForm(formData)
    return updateSignificantHazards.isPending || !validation.success
  }, [formData, updateSignificantHazards.isPending])

  const hasCurrentHazards = currentHazards.length > 0

  return (
    <>
      <Card inert={extraction.extractDialogOpen || undefined}>
        <CardHeader className='pb-2'>
          <div className='flex flex-row items-center justify-between gap-4'>
            <CardTitle>{t('detail.significantHazards')}</CardTitle>
            <div className='flex flex-wrap items-center gap-2'>
              <StandardSectionExtractControls
                hasPdf={hasPdf}
                extraction={extraction}
              />
              <SaveAsExtractionGoldButton
                userStandardId={standard.id}
                organizationId={standard.organizationId}
                extractionType='HAZARDS'
              />
              {hasCurrentHazards && (
                <Button variant='outline' size='sm' onClick={openAddDialog}>
                  {t('detail.addHazard')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {!hasCurrentHazards ? (
          <StandardSectionEmptyState
            icon={<AlertTriangle className='size-10' aria-hidden />}
            title={
              confirmedEmpty
                ? t('detail.empty.significantHazards.confirmedEmptyTitle')
                : t('detail.empty.significantHazards.title')
            }
            description={
              confirmedEmpty
                ? t('detail.empty.significantHazards.confirmedEmptyDescription')
                : hasPdf
                  ? t('detail.empty.significantHazards.descriptionWithPdf')
                  : t('detail.empty.significantHazards.descriptionNoPdf')
            }
            hasPdf={hasPdf}
            isPdfUploading={isPdfUploading}
            extraction={extraction}
            onUploadPdf={onUploadPdf}
            onManualAction={openAddDialog}
            manualActionLabel={t('detail.addHazard')}
          />
        ) : (
          <ul className='divide-y border-t pt-2'>
            {currentHazards.map((hazard) => {
              const availableLocales = resolveHazardAvailableLocales(hazard)
              const itemLocale = getDisplayLocale(hazard.id)
              return (
                <li
                  key={hazard.id}
                  className='flex flex-wrap items-start justify-between gap-2 px-4 py-2 first:pt-0'
                >
                  <div className='min-w-0 flex-1'>
                    {hazard.number != null && hazard.number !== '' && (
                      <span className='font-medium'>{hazard.number}. </span>
                    )}
                    <span className='text-sm whitespace-pre-wrap'>
                      {resolveLocalizedHazardField(
                        hazard,
                        itemLocale,
                        'description'
                      ) || '—'}
                    </span>
                  </div>
                  <div className='flex shrink-0 items-center gap-2'>
                    <StandardEntityLocaleToggle
                      availableLocales={availableLocales}
                      selectedLocale={itemLocale}
                      onSelectLocale={(locale) =>
                        selectItemLocale(hazard.id, locale)
                      }
                    />
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => openEditDialog(hazard)}
                      aria-label={t('detail.edit')}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            {updateSignificantHazards.isError && (
              <Alert variant='destructive'>
                <AlertDescription>
                  {updateSignificantHazards.error?.message ??
                    t('detail.saveError')}
                </AlertDescription>
              </Alert>
            )}
            <SignificantHazardForm data={formData} onChange={setFormData} />
          </div>
          <DialogFooter className='flex-row justify-between sm:justify-between'>
            <div>
              {isEdit && (
                <Button
                  type='button'
                  variant='destructive'
                  onClick={deleteCurrentHazard}
                  disabled={updateSignificantHazards.isPending}
                >
                  {t('detail.deleteHazard')}
                </Button>
              )}
            </div>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={closeDialog}
                disabled={updateSignificantHazards.isPending}
              >
                {t('detail.cancel')}
              </Button>
              <Button
                onClick={isEdit ? updateHazard : createHazard}
                disabled={isSaveDisabled}
              >
                {updateSignificantHazards.isPending
                  ? 'Saving…'
                  : t('detail.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
